import React from 'react';
import {
  default as MuiStepButton,
  type StepButtonProps as MuiStepButtonProps,
} from '@mui/material/StepButton';
import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';
import { DistributiveOmit } from '@mui/types';
import { ButtonBaseTypeMap } from '../ButtonBase/ButtonBase';

/**
 * Props that we do not want to include with StepButton.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
type StepButtonBlockedProps = keyof Pick<MuiStepButtonProps, 'icon'>;

export interface StepButtonTypeMap<
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {},
  RootComponent extends React.ElementType = ButtonBaseTypeMap['defaultComponent']
> {
  props: { href?: string } & AdditionalProps &
    DistributiveOmit<MuiStepButtonProps, StepButtonBlockedProps>;
  defaultComponent: RootComponent;
}

export type StepButtonProps<
  RootComponent extends React.ElementType = StepButtonTypeMap['defaultComponent'],
  // eslint-disable-next-line @typescript-eslint/ban-types
  AdditionalProps = {}
> = OverrideProps<
  StepButtonTypeMap<AdditionalProps, RootComponent>,
  RootComponent
> & {
  component?: React.ElementType;
};

/**
 * StepButton makes a Step clickable for non-linear navigation.
 *
 * Demos:
 *
 * - [Stepper](https://unify.workiva.org/components/stepper?tab=code)
 *
 * API:
 *
 * - [StepButton API](https://unify.workiva.org/components/stepper?tab=props#stepbutton)
 */
export const StepButton: OverridableComponent<StepButtonTypeMap> =
  MuiStepButton;
